var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React, { useEffect, useMemo, useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { avsCollectionSchema, billingZipOnlySchema } from "./avsCollectionSchema";
import { AddressField, ZipCodeField } from "./fields";
import { Form } from "radix-ui";
import { useCreditPaymentContext } from "../creditPayment";
import { useOrderIntent } from "../../providers";
import { RadixCheckbox } from "../primitives/RadixCheckbox";
import "../ui.css";
import "./avsCollection.css";
var getDeliveryAddressFromCart = function (cart, selectedAddress) {
    var _a;
    var sessionAddress = (_a = window.session) === null || _a === void 0 ? void 0 : _a.address;
    var cartAddress = selectedAddress
        || (cart === null || cart === void 0 ? void 0 : cart.address)
        || (cart === null || cart === void 0 ? void 0 : cart.address_tmp)
        || sessionAddress;
    if (!cartAddress || typeof cartAddress !== "object") {
        return null;
    }
    var lineOne = String(cartAddress.address || "").trim();
    var lineTwo = String(cartAddress.address2 || "").trim();
    var zipCode = String(cartAddress.zip || "").trim();
    var composedAddress = [lineOne, lineTwo].filter(Boolean).join(" ").trim();
    if (!composedAddress || !zipCode) {
        return null;
    }
    return {
        address: composedAddress,
        zipCode: zipCode,
    };
};
var isDeliverySelected = function (cart) {
    var _a;
    var serviceType = cart === null || cart === void 0 ? void 0 : cart.service_type;
    if (serviceType) {
        if (typeof serviceType.has_delivery !== "undefined") {
            return Boolean(Number(serviceType.has_delivery));
        }
        if (typeof serviceType.pos_id !== "undefined") {
            return Number(serviceType.pos_id) === 1;
        }
    }
    return Number((_a = window.session) === null || _a === void 0 ? void 0 : _a.service_type_id) === 1;
};
export var AVSCollectionDetails = function (_a) {
    var _b, _c, _d, _e;
    var onSubmit = _a.onSubmit, id = _a.id, defaultValues = _a.defaultValues, _f = _a.alwaysVisible, alwaysVisible = _f === void 0 ? false : _f, _g = _a.showAddress, showAddress = _g === void 0 ? true : _g, _h = _a.fieldAttributes, fieldAttributes = _h === void 0 ? {} : _h;
    var _j = useCreditPaymentContext(), setBillingInfo = _j.setBillingInfo, hasAvsConfigs = _j.hasAvsConfigs, billingInfo = _j.billingInfo, cart = _j.cart;
    var currentAddress = useOrderIntent().currentAddress;
    var deliveryAddress = useMemo(function () { return getDeliveryAddressFromCart(cart, currentAddress); }, [cart, currentAddress]);
    var canUseDeliveryAsBilling = isDeliverySelected(cart) && Boolean(deliveryAddress);
    var _k = useState(false), billingDifferentFromDelivery = _k[0], setBillingDifferentFromDelivery = _k[1];
    var validationSchema = showAddress ? avsCollectionSchema : billingZipOnlySchema;
    var methods = useForm({
        resolver: zodResolver(validationSchema),
        mode: "onChange",
        defaultValues: {
            address: (_c = (_b = defaultValues === null || defaultValues === void 0 ? void 0 : defaultValues.address) !== null && _b !== void 0 ? _b : billingInfo.billingAddress) !== null && _c !== void 0 ? _c : "",
            zipCode: (_e = (_d = defaultValues === null || defaultValues === void 0 ? void 0 : defaultValues.zipCode) !== null && _d !== void 0 ? _d : billingInfo.billingZipcode) !== null && _e !== void 0 ? _e : "",
        },
    });
    var handleSubmit = methods.handleSubmit, watch = methods.watch, setValue = methods.setValue;
    var address = watch("address");
    var zipCode = watch("zipCode");
    useEffect(function () {
        if (!canUseDeliveryAsBilling || billingDifferentFromDelivery || !deliveryAddress) {
            return;
        }
        if (showAddress) {
            setValue("address", deliveryAddress.address, { shouldValidate: true });
        }
        setValue("zipCode", deliveryAddress.zipCode, { shouldValidate: true });
    }, [billingDifferentFromDelivery, canUseDeliveryAsBilling, deliveryAddress, setValue, showAddress]);
    useEffect(function () {
        setBillingInfo({
            billingAddress: showAddress ? address !== null && address !== void 0 ? address : "" : "",
            billingZipcode: zipCode !== null && zipCode !== void 0 ? zipCode : "",
        });
    }, [address, showAddress, zipCode, setBillingInfo]);
    if (!alwaysVisible && !hasAvsConfigs) {
        return null;
    }
    var onSubmitWithBilling = function (data) {
        var _a, _b;
        setBillingInfo({
            billingAddress: showAddress ? (_a = data.address) !== null && _a !== void 0 ? _a : "" : "",
            billingZipcode: (_b = data.zipCode) !== null && _b !== void 0 ? _b : "",
        });
        onSubmit(data);
    };
    return (React.createElement(FormProvider, __assign({}, methods),
        React.createElement(Form.Root, { id: id, onSubmit: handleSubmit(onSubmitWithBilling), className: "radix-form radix-avs-collection-form", noValidate: true },
            React.createElement("h6", { className: "radix-avs-collection-title" }, showAddress ? "Enter your billing details." : "Enter your billing ZIP code."),
            canUseDeliveryAsBilling ? (React.createElement(RadixCheckbox, { id: "avs-use-different-billing", checked: billingDifferentFromDelivery, onCheckedChange: setBillingDifferentFromDelivery, label: showAddress
                    ? "Billing details are different from delivery address"
                    : "Billing ZIP code is different from delivery address" })) : null,
            showAddress ? (React.createElement(AddressField, __assign({}, fieldAttributes.address, { readOnly: canUseDeliveryAsBilling && !billingDifferentFromDelivery, "aria-readonly": canUseDeliveryAsBilling && !billingDifferentFromDelivery, className: canUseDeliveryAsBilling && !billingDifferentFromDelivery ? "avs-readonly-input" : undefined }))) : null,
            React.createElement(ZipCodeField, __assign({}, fieldAttributes.zipCode, { readOnly: canUseDeliveryAsBilling && !billingDifferentFromDelivery, "aria-readonly": canUseDeliveryAsBilling && !billingDifferentFromDelivery, className: canUseDeliveryAsBilling && !billingDifferentFromDelivery ? "avs-readonly-input" : undefined })))));
};
